import { useEffect } from 'react';
import { getSupabase, isSupabaseConfigured } from '../client';
import { invalidateCache } from '../cacheBus';

/**
 * Suscripción global a postgres_changes en gestiones, expedientes, visitas
 * e irregularidades. Cuando llega un evento (INSERT/UPDATE/DELETE),
 * invalidamos los caches de los hooks afectados → refetch automático en
 * todos los componentes que dependan.
 *
 * Pensado para montarse UNA VEZ en el shell de cada SPA (App.tsx o
 * AuthGate's children). Múltiples mounts crean canales duplicados;
 * mantenemos el unsubscribe en el cleanup. Si querés invalidación más
 * granular, llamar `invalidateCache(['key1','key2'])` directamente desde
 * el componente que ejecuta la mutación (ya hecho en CapturaPage).
 *
 * RLS sigue aplicando: el cliente Elawan solo recibe eventos de su tenant
 * (la sesión authenticated lleva el JWT con tenant_id, supabase-realtime
 * filtra server-side antes de mandar el payload).
 */
const TABLES = ['gestiones', 'expedientes', 'visitas', 'irregularidades'] as const;

/** Map de tabla → keys de useCachedFetch que se invalidan cuando cambia la tabla. */
const INVALIDATE_KEYS_BY_TABLE: Record<(typeof TABLES)[number], string[]> = {
  // Cambia gestion → cambian las vistas de parcelas (estado, firmado) y
  // los KPIs del dashboard cliente.
  gestiones: ['parcelas_tecnicas', 'parcelas_publicas', 'inventario', 'kpi_dashboard'],
  // Cambia expediente → ExpedientesPage refresca + contador en v_parcelas_tecnicas.
  expedientes: ['expedientes_list', 'parcelas_tecnicas'],
  // Visitas no afectan KPIs pero sí contadores.
  visitas: ['parcelas_tecnicas'],
  // Irregularidades afectan flag `irregular` en v_parcelas_tecnicas + KPIs.
  irregularidades: ['parcelas_tecnicas', 'parcelas_publicas', 'kpi_dashboard'],
};

export function useTablesRealtime(): void {
  useEffect(() => {
    if (!isSupabaseConfigured()) return;
    if (typeof window === 'undefined') return;

    const sb = getSupabase();
    // Un solo channel por SPA con todos los TABLES suscritos. Reduce
    // overhead vs un channel por tabla. supabase-js multiplexa internamente.
    const channel = sb.channel('aosenuma:tables-changes');

    for (const table of TABLES) {
      // supabase-js tipo postgres_changes es callable pero el overload
      // exhaustivo no se infiere bien sin generics; cast a any del callback
      // dentro evita el "no overload matches" sin perder type-safety en lo
      // que sí nos importa (invalidate keys).
      const onChange = (payload: { table?: string }) => {
        const t = payload?.table;
        if (!t) return;
        const keys = INVALIDATE_KEYS_BY_TABLE[t as keyof typeof INVALIDATE_KEYS_BY_TABLE];
        if (keys && keys.length > 0) {
          invalidateCache(keys);
        }
      };
      (channel as unknown as {
        on: (
          event: 'postgres_changes',
          filter: { event: '*'; schema: string; table: string },
          cb: (payload: { table?: string }) => void,
        ) => void;
      }).on('postgres_changes', { event: '*', schema: 'public', table }, onChange);
    }

    void channel.subscribe();

    return () => {
      void sb.removeChannel(channel);
    };
  }, []);
}
